import React, { useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";
import { GreshamHeader } from "../components/GreshamHeader";
import { GreshamFooter } from "../components/GreshamFooter";

export const DisclaimerContent: React.FC = () => {
  const textColor = useColorModeValue("gray.800", "white");

  return (
    <Box px={{ base: 6, md: 12, lg: 16 }} w="full" maxW="900px" mx="auto">
      <VStack align="stretch" spacing={6} color={textColor}>
        <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
          Mercurian Equity Pty Ltd (Mercurian / Mercurian Equity) is an investment management firm, focused on delivering investment solutions and strategic capital partnerships for global institutions and sophisticated clients. Mercurian advises certain sophisticated investors, private investment funds and institutional clients, and is not available to provide investment advisory or similar services to most other investors. This website is a resource for audiences other than investors such as potential employees, researchers, students, counterparties and industry participants. Mercurian believes it is useful for such persons to have an accurate source of relevant information. Under no circumstances should any information presented on this website be construed as an offer to sell, or solicitation of any offer to purchase, any securities or other investments. This website does not contain the information that an investor should consider or evaluate to make a potential investment. Offering materials relating to investments in entities managed by Mercurian are not available to the general public.
        </Text>

        <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
          To view this content, you must agree to the following terms, in addition to and supplementing the Mercurian Terms of Use and Privacy Policy:
        </Text>

        <Box>
          <VStack align="stretch" spacing={3} pl={4}>
            <Text as="div" fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
              I confirm to Mercurian Equity Pty Ltd and agree that:
            </Text>

            <Box>
              <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
                •	I am entering this website only to obtain general information regarding Mercurian Equity and not for any other purpose.
              </Text>
              <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
                •	I understand that investments managed by Mercurian Equity are not available to the general public.
              </Text>
              <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
                •	I understand that this website does not contain the information I would need to consider for an investment, and that such information is only available to a limited group of persons and institutions meeting specified criteria.
              </Text>
              <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
                •	I understand that this website has not been reviewed or approved by, filed with, or otherwise furnished to any governmental or similar authority, and is intended only to provide limited information to members of the public who have a legitimate interest in that information for reasons unrelated to making investments.
              </Text>
              <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
                •	I understand that when Mercurian Equity makes third party information available, Mercurian generally will not have verified statements made by the third party, and the presentation of information may omit important information.
              </Text>
              <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
                •	I understand that third party materials such as live interviews made available by Mercurian generally will not have been edited by Mercurian and statements in those materials by individuals associated with Mercurian should be understood in the conversational context in which they were made, which may include providing historical background.
              </Text>
              <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
                •	The content constitutes the proprietary intellectual property of Mercurian or its licensors and that I will not directly or indirectly copy, modify, recast, create derivative works, post, publish, display, redistribute, disclose, or make available the content, in whole or in part, to any third parties, or assist others to do the same, or otherwise make any commercial use of the content without the prior written consent of Mercurian Equity Pty Ltd.
              </Text>
            </Box>
          </VStack>
        </Box>

        <Text fontSize={{ base: "sm", md: "md" }} fontFamily="'Carmen Sans Light', sans-serif" lineHeight="1.7" fontWeight="300" opacity={0.95}>
          By registering my information below and clicking "Agree," I certify that I have read, understand and agree to the foregoing Disclaimer, Terms of Use and Privacy Policy.
        </Text>
      </VStack>
    </Box>
  );
};
const Disclaimer: React.FC = () => {
  const textColor = useColorModeValue("gray.800", "white");
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.pageYOffset;
        const rate = scrolled * -0.5;
        parallaxRef.current.style.transform = `translate3d(0, ${rate}px, 0)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box minHeight="100vh" bg="white" css={{ overscrollBehavior: "none", "-webkit-overflow-scrolling": "touch" }}>
      <GreshamHeader />

      {/* Hero Section with Parallax Background (matches PrivacyPolicy style) */}
      <Box
        as="section"
        w="full"
        position="relative"
        h="100vh"
        minH="500px"
        overflow="hidden"
        clipPath="inset(0)"
      >
        <Box
          ref={parallaxRef}
          position="fixed"
          top="0"
          left="0"
          right="0"
          height="120vh"
          backgroundImage="url('/background/polina-kuzovkova-sar-Y4BxZDA-unsplash.jpg')"
          backgroundSize="cover"
          backgroundPosition="center center"
          backgroundRepeat="no-repeat"
          filter="brightness(0.7)"
          willChange="transform"
        />

        <Box
          position={{ base: "absolute", lg: "absolute" }}
          top={{ base: "50%", lg: 0 }}
          left={{ base: 0, lg: 0 }}
          w={{ base: "full", lg: "70%" }}
          h={{ base: "auto", lg: "calc(120px + 300px)" }}
          display="flex"
          alignItems={{ base: "center", lg: "flex-end" }}
          justifyContent={{ base: "center", lg: "flex-start" }}
          zIndex={100}
          px={{ base: 6, sm: 8, md: 12, lg: 16 }}
          transform={{ base: "translateY(-50%)", lg: "none" }}
        >
          <VStack spacing={3} align="flex-start" maxW={{ base: "90%", sm: "80%", md: "700px", lg: "800px" }} ml={{ base: "auto", lg: "10%" }} mr="auto" textAlign="left" justify="center">
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              color="white"
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.1"
            >
              Disclaimer &amp; Agreement
            </Heading>
          </VStack>
        </Box>
      </Box>

      {/* Content Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 12, md: 16 }}
        px={{ base: 6, md: 12, lg: 20 }}
        bg="white"
        mt="-80px"
        position="relative"
        zIndex={20}
        borderRadius="3xl 3xl 0 0"
        boxShadow="0 -20px 40px rgba(0,0,0,0.05)"
      >
        <DisclaimerContent />
      </Box>

      <GreshamFooter />
    </Box>
  );
};

export default Disclaimer;
